import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';

/** Build a WhatsApp-formatted message describing the train.
 * Uses WhatsApp strikethrough syntax (~text~) for the scheduled time when the
 * train is delayed, followed by the new real-time. A ⚠️ emoji is prepended to
 * highlight delayed trains in the WhatsApp lock-screen preview. */
const buildMessage = ({ tab, station, train, t }) => {
  const isDep = tab === 'departures';
  const where = isDep ? `${station} → ${train.destination}` : `${train.origin} → ${station}`;
  const platformPart = train.platform && train.platform !== '—' ? ` • ${t('board.platform_label')} ${train.platform}` : '';
  const verb = isDep ? t('share.dep_at') : t('share.arr_at');

  const scheduled = train.time;
  const delayed = train.delay > 0 && train.realTime && train.realTime !== '—';

  const timeLine = delayed
    ? `${verb} ~${scheduled}~ ${train.realTime}`
    : `${verb} ${scheduled}`;

  // Arrival-at-destination is only set by the /api/commute endpoint (Mon trajet quotidien).
  const arrivalPart = train.arrival_at_dest && train.arrival_at_dest !== '—'
    ? ` • ${t('share.arr_expected')} ${train.arrival_at_dest}`
    : '';

  const prefix = delayed ? '⚠️ ' : '';

  return `${prefix}Info : Train Fluo ${train.number} — ${where}\n${timeLine}${arrivalPart}${platformPart}`;
};

const ShareTrain = ({ tab, station, train }) => {
  const { t } = useTranslation();
  // Guard against rapid double-tap which causes WhatsApp to *append* the second
  // wa.me payload onto the first draft (instead of overwriting).
  const lastClickRef = useRef(0);

  const onWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const now = Date.now();
    if (now - lastClickRef.current < 1500) return; // ignore rapid duplicates
    lastClickRef.current = now;

    const message = buildMessage({ tab, station, train, t });
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      onClick={onWhatsApp}
      title={t('share.whatsapp')}
      aria-label={t('share.whatsapp')}
      data-testid={`share-btn-${train.id || train.number}`}
      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
    >
      <MessageCircle className="w-4 h-4" />
    </button>
  );
};

export default ShareTrain;
